import Navbar from "@layout/Navbar";
import { useEffect, useState } from "react";

const SECTIONS = [
  {
    id: "identity", icon: "fa-fingerprint", color: "#2563EB", bg: "#EFF6FF",
    title: "Identity & access",
    content: `When you sign up for FirmConnector, we ask for your name, company name, and email address. That's just so you can personalize your new account, and we can send you invoices, updates, or other essential information. We'll never sell your personal info to third parties, and we won't use your name or company in marketing statements without your permission.\n\nYou can access or delete your personal information at any time by contacting us at info@firmconnector.com. We strive to keep your data accurate and up to date.`,
  },
  {
    id: "rights", icon: "fa-scale-balanced", color: "#7C3AED", bg: "#F5F3FF",
    title: "Your Rights With Respect to Your Information",
    content: `You may have heard about the General Data Protection Regulation (GDPR) in Europe. GDPR gives people under its protection certain rights with respect to their personal information:\n\n• Right to erasure: You have the right to request erasure of your personal information.\n• Right to access: You have the right to request an overview of your personal information.\n• Right to rectification: You have the right to request correction of your personal information.\n• Right to complain: You have the right to make a complaint about handling of your personal information.\n• Right to restrict processing: You have the right to request restriction of how your personal information is used.\n• Right to object: You have the right to object to how your personal information is used.\n• Right to portability: You have the right to request transfer of your data to another organization.`,
  },
  {
    id: "law", icon: "fa-gavel", color: "#F59E0B", bg: "#FEF3C7",
    title: "Law enforcement",
    content: `FirmConnector won't hand your data over to law enforcement unless a court order says we have to. We flat out reject requests from local and federal law enforcement when they seek data without a court order. And unless we're legally prevented from it, we'll always inform you when such requests are made.`,
  },
  {
    id: "security", icon: "fa-shield-halved", color: "#16A34A", bg: "#F0FDF4",
    title: "Security & Encryption",
    content: `All data is encrypted via SSL/TLS when transmitted from our servers to your browser. The database backups are also encrypted. We go to great lengths to secure your data at rest and in transit, following industry best practices at every level of our infrastructure.`,
  },
  {
    id: "deleted", icon: "fa-trash-can", color: "#DC2626", bg: "#FEF2F2",
    title: "Deleted data",
    content: `When you cancel your account, we'll ensure that nothing is deleted immediately. Your data is purged from our active databases within 30 days. If you delete your account, all of it will be purged within 60 days. After that, there will be no copies left anywhere in our systems.`,
  },
  {
    id: "location", icon: "fa-location-dot", color: "#06B6D4", bg: "#ECFEFF",
    title: "Location of Site and Data",
    content: `This Site is operated in Canada. If you are located in the European Union or elsewhere outside of Canada, please be aware that any information you provide to us will be transferred to Canada. By using the Site and providing us with your information, you consent to this transfer.`,
  },
  {
    id: "questions", icon: "fa-circle-question", color: "#EC4899", bg: "#FDF2F8",
    title: "Questions & changes",
    content: `FirmConnector may update this policy once in a blue moon — we'll notify you about significant changes by emailing the account owner or by posting a prominent notice on our site. You can access, change or delete your personal information at any time by contacting us.\n\nQuestions about this privacy policy? Please get in touch and we're happy to answer them.`,
  },
];

export default function PrivacyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [active, setActive] = useState("identity");

  const scrollTo = (id) => {
    setActive(id);
    const el = document.getElementById("section-" + id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC" }}>
      <Navbar solid />

      {/* ── PAGE HEADER ── */}
      <div style={{ paddingTop: 90, paddingBottom: 0, background: "#fff", borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(79,70,229,0.3)" }}>
              <i className="fa-solid fa-shield-halved" style={{ color: "#fff", fontSize: 22 }} />
            </div>
            <div>
              <h1 style={{ fontSize: "clamp(24px,4vw,36px)", fontWeight: 800, color: "#111827", margin: 0, letterSpacing: "-0.02em" }}>Privacy Policy</h1>
              <p style={{ fontSize: 14, color: "#9CA3AF", margin: "4px 0 0" }}>Last updated · 2025 · FirmConnector.com</p>
            </div>
          </div>

          {/* Section quick-nav pills */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingBottom: 16 }}>
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 99, border: `1px solid ${active === s.id ? s.color : "#E5E7EB"}`, background: active === s.id ? s.bg : "#fff", color: active === s.id ? s.color : "#6B7280", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.18s", fontFamily: "inherit" }}>
                <i className={`fa-solid ${s.icon}`} style={{ fontSize: 10 }} />
                {s.title.length > 20 ? s.title.slice(0, 20) + "…" : s.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px 80px" }}>
        <div className="row g-4 align-items-start">

          {/* Sticky sidebar */}
          <div className="col-lg-3 d-none d-lg-block">
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden", position: "sticky", top: 80, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #F3F4F6", background: "#F8FAFC" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em" }}>Contents</span>
              </div>
              {SECTIONS.map(s => (
                <button key={s.id} onClick={() => scrollTo(s.id)}
                  style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "10px 14px", border: "none", borderLeft: `3px solid ${active === s.id ? s.color : "transparent"}`, background: active === s.id ? s.bg : "transparent", cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.15s" }}
                  onMouseEnter={e => { if (active !== s.id) e.currentTarget.style.background = "#F8FAFC"; }}
                  onMouseLeave={e => { if (active !== s.id) e.currentTarget.style.background = "transparent"; }}>
                  <i className={`fa-solid ${s.icon}`} style={{ fontSize: 12, color: active === s.id ? s.color : "#D1D5DB", width: 14, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: active === s.id ? 700 : 400, color: active === s.id ? "#111827" : "#6B7280", lineHeight: 1.4 }}>
                    {s.title}
                  </span>
                </button>
              ))}

              <div style={{ padding: "14px 16px", borderTop: "1px solid #F3F4F6", background: "#F8FAFC" }}>
                <a href="mailto:info@firmconnector.com" style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#2563EB", fontWeight: 600, textDecoration: "none" }}>
                  <i className="fa-solid fa-envelope" style={{ fontSize: 11 }} /> Contact us
                </a>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="col-lg-9">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {SECTIONS.map((s, i) => (
                <div key={s.id} id={"section-" + s.id}
                  style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", scrollMarginTop: 90 }}>

                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 24px", borderBottom: "1px solid #F3F4F6", background: "#fff" }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fa-solid ${s.icon}`} style={{ color: s.color, fontSize: 16 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: "-0.01em" }}>{s.title}</h2>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.color}30`, borderRadius: 99, padding: "2px 9px" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Section body */}
                  <div style={{ padding: "20px 24px" }}>
                    {s.content.split("\n\n").map((para, j) =>
                      para.startsWith("•") ? (
                        <div key={j} style={{ display: "flex", flexDirection: "column", gap: 8, margin: "12px 0" }}>
                          {para.split("\n").filter(l => l.startsWith("•")).map((item, k) => (
                            <div key={k} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                              <div style={{ width: 20, height: 20, borderRadius: 6, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                                <i className="fa-solid fa-check" style={{ color: s.color, fontSize: 9 }} />
                              </div>
                              <span style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.75 }}>{item.replace("• ", "")}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p key={j} style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.85, marginBottom: 10 }}>{para}</p>
                      )
                    )}
                  </div>
                </div>
              ))}

              {/* Footer card */}
              <div style={{ background: "linear-gradient(135deg,#111827,#1A1040)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "28px 28px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <i className="fa-solid fa-envelope" style={{ color: "#fff", fontSize: 20 }} />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 4 }}>Questions about this policy?</div>
                  <div style={{ fontSize: 13, color: "#6B7280" }}>We're happy to answer any privacy-related questions.</div>
                </div>
                <a href="mailto:info@firmconnector.com"
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", borderRadius: 99, background: "rgba(255,255,255,0.08)", color: "#fff", fontWeight: 600, fontSize: 13, textDecoration: "none", border: "1px solid rgba(255,255,255,0.15)", transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}>
                  <i className="fa-solid fa-envelope" style={{ fontSize: 11 }} /> info@firmconnector.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
